import React from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { AutocompleteRenderOptionState } from '@material-ui/lab/Autocomplete';
import { useApi } from '@backstage/core-plugin-api';
import {
  DefaultEntityFilters,
  EntityAutocompletePicker,
  EntityFilter,
  catalogApiRef,
} from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';

/** The field the catalog indexes the department of a team under. */
export const DEPARTMENT_FIELD = 'spec.parent';

/**
 * Narrows a listing of teams to those of the departments picked.
 *
 * The field holds the vuid of the department, which is what the department
 * entity is named after, so the value of the filter is what the catalog is
 * asked for - no lookup in between.
 *
 * @public
 */
export class EntityDepartmentFilter implements EntityFilter {
  constructor(readonly values: string[]) {}

  getCatalogFilters(): Record<string, string | string[]> {
    return { [DEPARTMENT_FIELD]: this.values };
  }

  toQueryValue(): string[] {
    return this.values;
  }
}

/** The filters of a listing of teams that can be narrowed by department. */
export type DepartmentEntityFilters = DefaultEntityFilters & {
  departments?: EntityDepartmentFilter;
};

/**
 * The titles of the departments, by vuid. Teams name their department by vuid
 * only, so whatever shows one to a reader looks its title up here.
 *
 * @public
 */
export const useDepartmentTitles = (): Record<string, string | undefined> => {
  const catalogApi = useApi(catalogApiRef);
  const { value } = useAsync(async () => {
    const { items } = await catalogApi.getEntities({
      filter: { kind: 'Group', 'spec.type': 'department' },
      fields: ['metadata.name', 'metadata.title'],
    });
    return Object.fromEntries(
      items.map(department => [
        department.metadata.name,
        department.metadata.title,
      ]),
    ) as Record<string, string | undefined>;
  }, [catalogApi]);
  return value ?? {};
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/**
 * Props for {@link DepartmentPicker}.
 *
 * @public
 */
export type DepartmentPickerProps = {
  /** See {@link useDepartmentTitles}. */
  titles: Record<string, string | undefined>;
  /**
   * The names of the filters that decide which departments are offered, the
   * kind and the type unless told otherwise. A department none of the listed
   * teams belongs to would only empty the listing, so a page that narrows its
   * teams further names its own filters here too.
   */
  filtersForAvailableValues?: string[];
};

/**
 * Picks the departments whose teams are listed.
 *
 * @public
 */
export const DepartmentPicker = (props: Readonly<DepartmentPickerProps>) => {
  const { titles, filtersForAvailableValues = ['kind', 'type'] } = props;

  // Same format as the platform team picker: the title for the reader, the
  // vuid for whoever has it from a ticket or a CMDB query.
  const optionLabel = (vuid: string) => {
    const title = titles[vuid];
    return title && title !== vuid ? `${title} (${vuid})` : vuid;
  };

  return (
    <EntityAutocompletePicker<DepartmentEntityFilters, 'departments'>
      label="Department"
      name="departments"
      path={DEPARTMENT_FIELD}
      Filter={EntityDepartmentFilter}
      // The filters of the page are its own business, the picker only hands
      // their names on.
      filtersForAvailableValues={
        filtersForAvailableValues as Array<keyof DepartmentEntityFilters>
      }
      // The stock option renderer prints the raw facet value and ignores
      // `getOptionLabel`, so the dropdown needs its own row to show the name.
      getOptionLabel={optionLabel}
      renderOption={(
        option: string,
        { selected }: AutocompleteRenderOptionState,
      ) => (
        <FormControlLabel
          control={
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              checked={selected}
            />
          }
          label={optionLabel(option)}
          onClick={event => event.preventDefault()}
        />
      )}
    />
  );
};
